// Lazy import Sentry functions to avoid blocking bundle splitting
const getSentryFunctions = async () => {
  const sentry = await import('./sentry');
  return {
    addSentryBreadcrumb: sentry.addSentryBreadcrumb
  };
};

interface PerformanceMetrics {
  pageLoadTime: number;
  domContentLoaded: number;
  firstContentfulPaint?: number;
  largestContentfulPaint?: number;
  cumulativeLayoutShift?: number;
  firstInputDelay?: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics = {
    pageLoadTime: 0,
    domContentLoaded: 0
  };

  constructor() {
    this.initializePerformanceObserver();
    this.measurePageLoad();
  }

  private initializePerformanceObserver() {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    try {
      // Observe Core Web Vitals
      if ('PerformanceObserver' in window) {
        // Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver(async (list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as any;
          this.metrics.largestContentfulPaint = lastEntry.startTime;
          try {
            const { addSentryBreadcrumb } = await getSentryFunctions();
            addSentryBreadcrumb(`LCP: ${lastEntry.startTime.toFixed(2)}ms`, 'performance', 'info');
          } catch (error) {
            console.warn('Failed to log LCP breadcrumb:', error);
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver(async (list) => {
          const entries = list.getEntries();
          entries.forEach(async (entry: any) => {
            this.metrics.firstInputDelay = entry.processingStart - entry.startTime;
            try {
              const { addSentryBreadcrumb } = await getSentryFunctions();
              addSentryBreadcrumb(`FID: ${this.metrics.firstInputDelay.toFixed(2)}ms`, 'performance', 'info');
            } catch (error) {
              console.warn('Failed to log FID breadcrumb:', error);
            }
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });

        // Cumulative Layout Shift (CLS)
        const clsObserver = new PerformanceObserver(async (list) => {
          let clsValue = 0;
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.metrics.cumulativeLayoutShift = clsValue;
          try {
            const { addSentryBreadcrumb } = await getSentryFunctions();
            addSentryBreadcrumb(`CLS: ${clsValue.toFixed(4)}`, 'performance', 'info');
          } catch (error) {
            console.warn('Failed to log CLS breadcrumb:', error);
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      }

      // First Contentful Paint
      if ('performance' in window && 'getEntriesByType' in performance) {
        const paintEntries = performance.getEntriesByType('paint');
        const fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');
        if (fcpEntry) {
          this.metrics.firstContentfulPaint = fcpEntry.startTime;
          try {
            getSentryFunctions().then(({ addSentryBreadcrumb }) => {
              addSentryBreadcrumb(`FCP: ${fcpEntry.startTime.toFixed(2)}ms`, 'performance', 'info');
            }).catch(error => {
              console.warn('Failed to log FCP breadcrumb:', error);
            });
          } catch (error) {
            console.warn('Failed to load Sentry for FCP:', error);
          }
        }
      }
    } catch (error) {
      console.warn('Performance monitoring setup failed:', error);
    }
  }

  private measurePageLoad() {
    if (typeof window === 'undefined') return;

    // Measure page load time
    window.addEventListener('load', async () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        this.metrics.pageLoadTime = navigation.loadEventEnd - navigation.fetchStart;
        this.metrics.domContentLoaded = navigation.domContentLoadedEventEnd - navigation.fetchStart;
        
        try {
          const { addSentryBreadcrumb } = await getSentryFunctions();
          addSentryBreadcrumb(`Page Load: ${this.metrics.pageLoadTime.toFixed(2)}ms`, 'performance', 'info');
          addSentryBreadcrumb(`DOM Ready: ${this.metrics.domContentLoaded.toFixed(2)}ms`, 'performance', 'info');
        } catch (error) {
          console.warn('Failed to log page load breadcrumbs:', error);
        }
        
        // Log performance metrics for debugging
        console.log('📊 Performance Metrics:', this.metrics);
      }
    });
  }

  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  public measureRouteChange(routeName: string) {
    const startTime = performance.now();
    
    return async () => {
      const endTime = performance.now();
      const routeLoadTime = endTime - startTime;
      try {
        const { addSentryBreadcrumb } = await getSentryFunctions();
        addSentryBreadcrumb(`Route ${routeName} loaded in ${routeLoadTime.toFixed(2)}ms`, 'navigation', 'info');
      } catch (error) {
        console.warn('Failed to log route change breadcrumb:', error);
      }
      console.log(`🚀 Route "${routeName}" loaded in ${routeLoadTime.toFixed(2)}ms`);
    };
  }

  public measureComponentRender(componentName: string) {
    const startTime = performance.now();
    
    return async () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      if (renderTime > 100) { // Only log slow renders
        try {
          const { addSentryBreadcrumb } = await getSentryFunctions();
          addSentryBreadcrumb(`Slow render: ${componentName} took ${renderTime.toFixed(2)}ms`, 'performance', 'warning');
        } catch (error) {
          console.warn('Failed to log slow render breadcrumb:', error);
        }
        console.warn(`⚠️ Slow render: "${componentName}" took ${renderTime.toFixed(2)}ms`);
      }
    };
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Utility functions for measuring performance
export const measureRouteChange = (routeName: string) => performanceMonitor.measureRouteChange(routeName);
export const measureComponentRender = (componentName: string) => performanceMonitor.measureComponentRender(componentName);
export const getPerformanceMetrics = () => performanceMonitor.getMetrics();

// Resource loading optimization utilities
export const preloadRoute = (routePath: string) => {
  if (typeof window === 'undefined') return;
  
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = routePath;
  document.head.appendChild(link);
};

export const preloadImage = (src: string) => {
  if (typeof window === 'undefined') return;
  
  const img = new Image();
  img.src = src;
};

// Critical resource hints
export const addResourceHints = () => {
  if (typeof window === 'undefined') return;
  
  // DNS prefetch for external resources
  const dnsPrefetchDomains = [
    'fonts.googleapis.com',
    'fonts.gstatic.com'
  ];
  
  dnsPrefetchDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = `//${domain}`;
    document.head.appendChild(link);
  });
};